import { useState } from "react";

import { useMutation as defaultUseMutation } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { ResultAsync } from "neverthrow";

import { changeAccountPlan as ChangeAccountPlanData } from "@zapier/checkout/esm/graphql-types";
import { ApolloInjection, MutateFunction } from "@zapier/checkout/esm/types";

/**
 * The entities returned from this mutation are purely for updating the Apollo cache, which in turn updates the sidebar's plan and usage information.
 * @see https://www.apollographql.com/blog/demystifying-cache-normalization/ for why this automagically works.
 */
export const CHANGE_PLAN = gql`
  mutation changeAccountPlan(
    $accountId: Int!
    $inputData: ChangeAccountPlanInput!
  ) {
    changeAccountPlan(id: $accountId, input: $inputData) {
      id
      currentPlan: planV2 {
        id
      }
      plan {
        id
        allowCompanies
        allowImportExport
        amount
        hasMeteredBilling
        hasPremiumApps
        hasTeams
        name
        tasks
        tiers {
          numTasks
          pricePerTask
        }
      }
      usage {
        periodEnd
        tasks {
          limit
          total
        }
        zaps {
          limit
          on
        }
      }
    }
  }
`;

type Input = {
  accountId: number | undefined;
  planId: string | number | undefined;
  downgradeReason: string | null | undefined;
  currency: string | null | undefined;
  invoicedAt: string | null | undefined;
  paymentIntentId?: string | null | undefined;
} & ApolloInjection;

export const useChangePlan = ({
  accountId,
  planId,
  downgradeReason,
  currency,
  invoicedAt,
  paymentIntentId,
  useMutation = defaultUseMutation,
}: Input) => {
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [hasChangedPlan, setHasChangedPlan] = useState(false);
  const [changePlanError, setChangePlanError] = useState("");

  const inputData = {
    planId,
    downgradeReason,
    currency,
    invoiced_at: invoicedAt,
    paymentIntentId,
  };

  const [changePlanMutation]: [MutateFunction<ChangeAccountPlanData>, {}] =
    useMutation(CHANGE_PLAN, {
      variables: {
        accountId,
        inputData,
      },
    });

  const formatError = (errorMessage: string) => {
    const error = errorMessage.replace("GraphQL error: ", "");
    return error === "Exchange rate no longer valid"
      ? "This preview has expired. Please refresh and try again."
      : error;
  };

  const changePlan = () => {
    const changePlanPromise = async () => {
      setChangePlanError("");
      setIsChangingPlan(true);
      try {
        await changePlanMutation();
        setHasChangedPlan(true);
        localStorage.removeItem("invoicedAtDate");
      } catch (e) {
        setChangePlanError(formatError((e as Error).message));
        throw e;
      } finally {
        setIsChangingPlan(false);
      }
    };
    return ResultAsync.fromPromise(changePlanPromise(), e => e);
  };

  return {
    changePlan,
    changePlanError,
    hasChangedPlan,
    isChangingPlan,
  };
};
